/**
 * useTapToggle — a hook that detects N rapid taps within a time window and
 * returns a callback to attach to a tappable element. Used for hidden dev
 * tooling (e.g. tap the page title 10 times in 6 seconds to toggle the
 * DevPanel on mobile, where Ctrl+Shift+K and ?dev=1 aren't practical —
 * especially in an iOS PWA where the URL bar isn't visible).
 *
 * Returns an onClick handler. Attach it to any element's onClick.
 */
import { useRef, useCallback } from 'react';

export function useTapToggle(count: number = 10, windowMs: number = 6000): () => void {
  const taps = useRef<number[]>([]);

  return useCallback(() => {
    const now = Date.now();
    // Drop taps outside the window.
    taps.current = taps.current.filter((t) => now - t < windowMs);
    taps.current.push(now);
    if (taps.current.length >= count) {
      taps.current = [];
      // Dispatch a custom event the Book page can listen for. Using an event
      // rather than a direct callback keeps the hook generic (the caller
      // decides what to do when the threshold is reached).
      window.dispatchEvent(new CustomEvent('itk-dev-tap-toggle'));
    }
  }, [count, windowMs]);
}